/**
 * What a line on a page's map stands for: the page a click on it opens (its `slug`, `port_slugs`
 * being that page's ports, see `RouteProperties`). Hover and click ask the same question here, so
 * what lights up is what the click shows (but a click on the page being shown opens nothing).
 */

/** A line feature as MapLibre hands it back. */
interface Line {
  properties: Record<string, unknown>;
}

/** A feature's ports: MapLibre gives an array property back as an array, or as its JSON text. */
export function portSlugsOf(line: Line): string[] {
  const slugs = line.properties.port_slugs;
  if (Array.isArray(slugs)) return slugs as string[];
  if (typeof slugs !== 'string') return [];
  try {
    return JSON.parse(slugs) as string[];
  } catch {
    return [];
  }
}

/** The line under the cursor that stands for the rest: the one whose page has the most stops, the first of equals. */
export function lineUnder<L extends Line>(lines: L[]): L | undefined {
  let best: L | undefined;
  for (const line of lines) {
    if (line.properties.slug && (!best || portSlugsOf(line).length > portSlugsOf(best).length)) best = line;
  }
  return best;
}

/** The page a click on these lines under the cursor opens (`lineUnder`'s); none when it is `shown`, the route page being shown. */
export function pageUnder(lines: Line[], shown?: string): string | undefined {
  const slug = lineUnder(lines)?.properties.slug as string | undefined;
  return slug === shown ? undefined : slug;
}

/**
 * What the page points at under the mouse (`focus` in map.ts): ports by slug, routes by id; from a line on the map, also
 * the page it opens (`page`), so only that route's lines opening it light there, not its way back.
 */
export interface Focus {
  ports: string[];
  routes: number[];
  page?: string;
  /** From a destination row on a port page: the destination, only its routes' legs to it lit (their `to`), not the whole routes. */
  to?: string;
}

/** Hovering lines under the cursor: the one a click opens (`lineUnder`), its route alone. */
export function lineFocus(lines: Line[]): Focus | null {
  const line = lineUnder(lines);
  return line ? { ports: [], routes: [line.properties.id as number], page: line.properties.slug as string } : null;
}

/**
 * Hovering a port: the port, and but on a route page (where every line would) the routes of `lines` a boat leaving it
 * sails, as its page draws them: through it, but a line sailed one way (`one_way`) that ends there, not as a loop.
 */
export function portFocus(slug: string, lines: Line[], routePage: boolean): Focus {
  const leaving = (line: Line) => {
    const stops = portSlugsOf(line);
    return stops.includes(slug) && !(line.properties.one_way && stops.at(-1) === slug && stops[0] !== slug);
  };
  const routes = routePage ? [] : lines.filter(leaving).map((line) => line.properties.id as number);
  return { ports: [slug], routes: [...new Set(routes)] };
}

/** What `focus` lights among `lines`: those of its routes (opening its page, when it has one; or their legs to `to`), and the ports they and it name. */
export function lit(focus: Focus | null, lines: Line[]): { lines: Line[]; ports: string[] } {
  if (!focus) return { lines: [], ports: [] };
  const shown = lines.filter(
    (line) =>
      focus.routes.includes(line.properties.id as number) &&
      (!focus.page || line.properties.slug === focus.page) &&
      (!focus.to || line.properties.to === focus.to),
  );
  // With `to`, a leg's own ports (`stops`), else its whole route's
  const ports = shown.flatMap((line) => (focus.to ? (line.properties.stops as string[]) : portSlugsOf(line)));
  return { lines: shown, ports: [...new Set([...focus.ports, ...ports])] };
}
